import { Head } from "@inertiajs/react";
import { AnimatePresence, motion } from "framer-motion";
import { CircleAlertIcon, LockIcon, WandSparklesIcon } from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	BulkActionButtons,
	ConfirmationModal,
	FormActionButtons,
	RescheduleAppointmentRow,
} from "@/components/admin-portal/appointment/bulk-reschedule";
import type { MitraReasonProp } from "@/components/admin-portal/appointment/mitra-assignment-reason";
import { ScheduleOverview } from "@/components/admin-portal/appointment/schedule-overview";
import {
	FormPageContainer,
	FormPageHeaderGridPattern,
} from "@/components/admin-portal/shared/page-layout";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useBulkRescheduleForm } from "@/hooks/admin-portal/appointment/use-bulk-reschedule-form";
import { getGenderIcon } from "@/hooks/use-gender";
import type { BulkRescheduleItemSchema } from "@/lib/appointments/form-bulk-reschedule";
import type { PREFERRED_THERAPIST_GENDER } from "@/lib/constants";
import { GENDER_LABELS } from "@/lib/constants";
import { toIsoDate } from "@/lib/therapist-scheduling/date";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";

interface OrderDetail {
	id: string;
	registrationNumber: string;
	patient: {
		id: string;
		name: string;
		gender?: string;
	} | null;
	package: {
		id: number | string;
		name: string;
		numberOfVisit: number;
	} | null;
	service: {
		id: number | string;
		name: string;
	} | null;
	appointments: Array<
		Appointment & {
			serviceName?: string;
			locationCity?: string;
			// Serialized for the calendar SchedulePicker (see
			// PreparationBulkRescheduleService#serialize_order_detail).
			locationId?: number | string | null;
			serviceId?: number | string | null;
			latitude?: number | null;
			longitude?: number | null;
		}
	>;
}

export interface BulkReschedulePageProps {
	order: OrderDetail;
	mitraReason?: MitraReasonProp;
	optionsData?: {
		preferredTherapistGender: typeof PREFERRED_THERAPIST_GENDER;
		therapists?: Array<NonNullable<BulkRescheduleItemSchema["therapist"]>>;
		apptDateTime: {
			max: string | null;
			min: string | null;
			message: string | null;
		};
	};
}

export interface BulkRescheduleGlobalPageProps
	extends BaseGlobalPageProps,
		BulkReschedulePageProps {
	[key: string]: any;
	errors: Record<string, string[]> & {
		fullMessages: string[];
	};
}

export default function BulkReschedulePage({
	order,
	optionsData,
}: BulkReschedulePageProps) {
	// --- 1. Hooks & Core Form Context ---
	const { t } = useTranslation("appointments-form");
	const {
		form,
		isLoading,
		hasChanges,
		changedVisits,
		isConfirmOpen,
		openConfirmModal,
		closeConfirmModal,
		onConfirmSubmit,
		applyTherapistToAll,
		resetAll,
		getSummary,
		appointments,
		pageURL,
		watchReason,
		setReason,
		watchSendEmail,
		setSendEmail,
		watchBypassSlotConflict,
		setBypassSlotConflict,
		watchBypassContractCheck,
		setBypassContractCheck,
		watchBypassJourneyCheck,
		setBypassJourneyCheck,
		mitraReason,
	} = useBulkRescheduleForm();

	// --- 2. Observation & Derived State ---
	const fields = form.watch("rescheduleRequests");
	// Continuity anchor: the WHOLE series follows the therapist of the FIRST ROW,
	// i.e. the chronologically-earliest visit after auto-sort. Each visit may still
	// be ASSIGNED a different therapist — this only drives the calendar guidance.
	// Following the top row (not the original visit 1) means that if the earliest
	// visit is itself rescheduled/reassigned, continuity follows the new lead visit.
	const continuityByIndex = useMemo(() => {
		const seriesAnchor = fields[0]?.therapist ?? null;
		return fields.map(() => seriesAnchor);
	}, [fields]);

	// Dates the OTHER rows currently hold, per row — struck through in each row's
	// calendar. Derived from live form state, not the server, so a date picked in
	// one row shows up immediately in the others.
	const scheduledDatesByIndex = useMemo(() => {
		const isoDates = fields.map((request) =>
			request.appointmentDateTime
				? toIsoDate(new Date(request.appointmentDateTime))
				: null,
		);
		return isoDates.map((_, index) =>
			isoDates.filter(
				(date, other): date is string => other !== index && !!date,
			),
		);
	}, [fields]);
	const headTitle = useMemo(
		() => `Bulk Reschedule — ${order.registrationNumber}`,
		[order.registrationNumber],
	);
	const summary = useMemo(() => getSummary(), [getSummary]);

	const orderOverview = useMemo(() => {
		const firstAppointment = order.appointments[0];
		const serviceName =
			firstAppointment?.serviceName?.replaceAll("_", " ") || "N/A";
		const cityName = firstAppointment?.locationCity || "N/A";
		const totalVisits = order.package?.numberOfVisit ?? appointments.length;

		const genderInfo = GENDER_LABELS.find(
			(g: any) => g.title === order.patient?.gender,
		);
		const patientNameWithPrefix =
			`${genderInfo?.titlePrefix || ""} ${order.patient?.name || "N/A"}`.trim();

		return {
			registrationNumber: order.registrationNumber,
			patientName: patientNameWithPrefix,
			packageName: order.package?.name,
			serviceName,
			cityName,
			totalVisits,
		};
	}, [
		order.registrationNumber,
		order.patient?.name,
		order.patient?.gender,
		order.package?.name,
		order.package?.numberOfVisit,
		appointments,
		order.appointments,
	]);

	// --- 3. Core Handlers ---
	const handleUpdateField = useCallback(
		(index: number, field: string, value: unknown) => {
			const currentRequests = form.getValues("rescheduleRequests");
			const updatedRequests = currentRequests.map((req, i) => {
				if (i !== index) return req;
				return { ...req, [field]: value };
			});
			form.setValue("rescheduleRequests", updatedRequests, {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[form],
	);

	const handleUpdateAllFields = useCallback(
		(requests: BulkRescheduleItemSchema[]) => {
			form.setValue("rescheduleRequests", requests, {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[form],
	);

	// --- 4. Chronological Sorting System ---
	const [sortConfirmation, setSortConfirmation] = useState<{
		index: number;
		newDateTime: Date | null;
	} | null>(null);

	const [unscheduleConfirmation, setUnscheduleConfirmation] = useState<{
		index: number;
		shouldSort: boolean;
	} | null>(null);

	const handleProcessDateTimeChange = useCallback(
		(index: number, newDateTime: Date | null) => {
			// 1. Prepare proposed dates to check for sorting
			const proposedDates = fields.map((f, i) =>
				i === index ? newDateTime : f.appointmentDateTime,
			);

			// 2. Validate chronological order
			let isSorted = true;
			for (let i = 0; i < proposedDates.length - 1; i++) {
				const a = proposedDates[i];
				const b = proposedDates[i + 1];

				if (a === null && b !== null) {
					isSorted = false;
					break;
				}

				if (
					a instanceof Date &&
					b instanceof Date &&
					!Number.isNaN(a.getTime()) &&
					!Number.isNaN(b.getTime()) &&
					a.getTime() > b.getTime()
				) {
					isSorted = false;
					break;
				}
			}

			// 3. Handle Unschedule (null) case
			if (newDateTime === null) {
				const hasTherapist = !!fields[index].therapist;
				// If it has a therapist OR it triggers an out-of-order state, show warning
				if (hasTherapist || !isSorted) {
					setUnscheduleConfirmation({ index, shouldSort: !isSorted });
					return;
				}
				// Otherwise, just unschedule directly
				handleUpdateField(index, "appointmentDateTime", null);
				return;
			}

			// 4. Handle sorting violation for valid dates
			if (!isSorted) {
				setSortConfirmation({ index, newDateTime });
				return;
			}

			// 5. Apply valid date change
			handleUpdateField(index, "appointmentDateTime", newDateTime);
		},
		[fields, handleUpdateField],
	);

	/**
	 * Executes the chronological re-sorting of all visits.
	 * This is called when the user confirms the "Yes, Apply and Sort" action.
	 */
	const handleConfirmSort = useCallback(() => {
		if (sortConfirmation) {
			const { index, newDateTime } = sortConfirmation;

			// 1. Prepare the next state with the updated date
			const nextFields = fields.map((f, i) => {
				if (i === index) {
					return { ...f, appointmentDateTime: newDateTime };
				}
				return f;
			});

			// 2. Sort the entire field objects by date
			nextFields.sort((a, b) => {
				const dateA = a.appointmentDateTime;
				const dateB = b.appointmentDateTime;

				if (!dateA && !dateB) return 0;
				if (!dateA) return 1;
				if (!dateB) return -1;
				return dateA.getTime() - dateB.getTime();
			});

			// 3. Batch update the form with the new reordered fields
			handleUpdateAllFields(nextFields);
			setSortConfirmation(null);
		}
	}, [sortConfirmation, fields, handleUpdateAllFields]);

	/**
	 * Executes the clearing of both date/time and therapist when unscheduling.
	 */
	const handleConfirmUnschedule = useCallback(() => {
		setUnscheduleConfirmation((prev) => {
			if (!prev) return null;

			const { index, shouldSort } = prev;

			// 1. Prepare the next state by clearing target visit
			const nextFields = fields.map((f, i) => {
				if (i !== index) return f;
				return {
					...f,
					appointmentDateTime: null,
					therapist: null,
					preferredTherapistGender: "NO PREFERENCE",
				};
			});

			if (shouldSort) {
				// 2. Sort the entire field objects (nulls move to end)
				nextFields.sort((a, b) => {
					const dateA = a.appointmentDateTime;
					const dateB = b.appointmentDateTime;

					if (!dateA && !dateB) return 0;
					if (!dateA) return 1;
					if (!dateB) return -1;
					return dateA.getTime() - dateB.getTime();
				});
			}

			// 3. Update the form
			handleUpdateAllFields(nextFields);
			return null;
		});
	}, [fields, handleUpdateAllFields]);

	// --- Bulk preferred gender (one control for every visit) ---
	// Confirmed before it clears assigned therapists (see gender_confirm_desc_bulk).
	const [pendingBulkGender, setPendingBulkGender] = useState<string | null>(
		null,
	);
	const bulkGender = fields[0]?.preferredTherapistGender ?? "NO PREFERENCE";
	const applyBulkGender = useCallback(
		(value: string, resetTherapist: boolean) => {
			const nextFields = fields.map((f) => ({
				...f,
				preferredTherapistGender: value,
				...(resetTherapist ? { therapist: null } : {}),
			}));
			handleUpdateAllFields(nextFields);
		},
		[fields, handleUpdateAllFields],
	);
	const handleBulkGenderChange = useCallback(
		(value: string) => {
			if (value === bulkGender) return;
			if (fields.some((f) => !!f.therapist)) {
				setPendingBulkGender(value);
				return;
			}
			applyBulkGender(value, false);
		},
		[bulkGender, fields, applyBulkGender],
	);

	return (
		<Fragment>
			<Head title={headTitle} />

			<FormPageContainer>
				<section className="flex flex-col justify-center gap-4 mx-auto md:gap-6 w-12/12 xl:w-10/12">
					<FormPageHeaderGridPattern
						title={t("bulk_reschedule.head_title")}
						description={t("bulk_reschedule.page_description")}
					/>

					<ScheduleOverview
						registrationNumber={orderOverview?.registrationNumber}
						patientName={order.patient?.name}
						patientGender={order.patient?.gender}
						cityName={orderOverview?.cityName}
						serviceName={orderOverview?.serviceName}
						packageName={orderOverview?.packageName}
						totalVisits={orderOverview?.totalVisits}
					/>

					<div className="flex flex-wrap items-center justify-between gap-3 p-3 border-2 border-dashed bg-card">
						<div className="text-sm text-muted-foreground flex items-center">
							<WandSparklesIcon className="shrink-0 inline-block size-4 mr-2 opacity-75" />
							<span>{t("bulk_reschedule.actions.apply_bulk")}</span>
						</div>

						<BulkActionButtons onApplyTherapistToAll={applyTherapistToAll} />
					</div>

					{/* Bulk preferred therapist gender: one control drives every visit's
					    search & picker. "Apply Therapist to All" also sets this to the
					    applied therapist's gender. */}
					<div className="space-y-3 p-3 border rounded-md bg-muted/50">
						<Label className="font-medium text-sm">
							{t(
								"appt_schedule.fields.pref_therapist_gender.label",
								"Preferred Therapist Gender",
							)}
						</Label>
						<RadioGroup
							value={bulkGender}
							onValueChange={handleBulkGenderChange}
							orientation="horizontal"
							className="grid grid-cols-1 gap-4 md:grid-cols-3"
						>
							{(optionsData?.preferredTherapistGender ?? []).map((gender) => (
								<Label
									key={gender}
									className="flex cursor-pointer items-start gap-3 p-3 border rounded-md shadow-inner border-input bg-sidebar"
								>
									<RadioGroupItem value={gender} />
									<span className="flex items-center gap-1 font-normal uppercase truncate">
										{getGenderIcon(gender, "shrink-0")}
										<span className="truncate text-nowrap">
											{t(
												`appt_schedule.fields.pref_therapist_gender.options.${gender.toLowerCase()}`,
												gender,
											)}
										</span>
									</span>
								</Label>
							))}
						</RadioGroup>
					</div>

					<AlertDialog
						open={!!pendingBulkGender}
						onOpenChange={(open) => !open && setPendingBulkGender(null)}
					>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t(
										"appt_schedule.schedule_picker.common.gender_confirm_title",
									)}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t(
										"appt_schedule.schedule_picker.common.gender_confirm_desc_bulk",
									)}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel onClick={() => setPendingBulkGender(null)}>
									{t("button.cancel", "Cancel")}
								</AlertDialogCancel>
								<AlertDialogAction
									onClick={() => {
										if (pendingBulkGender)
											applyBulkGender(pendingBulkGender, true);
										setPendingBulkGender(null);
									}}
								>
									{t("appt_schedule.schedule_picker.common.continue")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>

					<Form {...form}>
						<form
							id="bulk-reschedule-form"
							onSubmit={form.handleSubmit(openConfirmModal)}
							className="space-y-6"
						>
							<div className="space-y-2 relative">
								<AnimatePresence mode="popLayout">
									{fields.map((field, index) => {
										const appointment = appointments.find(
											(a) => a.id === field.appointmentId,
										);
										if (!appointment) return null;

										// Read-only rather than absent: the visit is part of the
										// series and hiding it would make the order look shorter
										// than it is. Nothing here can change, so the visit never
										// enters the changed-visits payload.
										if (appointment.documentsLocked) {
											return (
												<motion.div
													key={appointment.id}
													layout
													initial={{ opacity: 0, y: 10 }}
													animate={{ opacity: 1, y: 0 }}
													exit={{ opacity: 0, scale: 0.95 }}
													className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-muted/40 px-4 py-3"
												>
													<LockIcon
														className="size-4 flex-shrink-0 text-muted-foreground"
														aria-hidden="true"
													/>
													<span className="text-sm font-medium">
														{t("bulk_reschedule.visit_label", {
															defaultValue: "Visit {{number}}",
															number: index + 1,
														})}
													</span>
													<span className="text-sm text-muted-foreground">
														{t("bulk_reschedule.documents_locked", {
															defaultValue:
																"Completed with SOAP and evidence — this visit can no longer be rescheduled.",
														})}
													</span>
												</motion.div>
											);
										}

										return (
											<motion.div
												key={appointment.id}
												layout
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, scale: 0.95 }}
												transition={{
													type: "spring",
													stiffness: 500,
													damping: 50,
													mass: 1,
												}}
											>
												<RescheduleAppointmentRow
													appointment={appointment as any}
													field={field}
													index={index}
													currentVisitNumber={index + 1}
													otherScheduledDates={
														scheduledDatesByIndex[index] ?? []
													}
													continuityTherapist={continuityByIndex[index] ?? null}
													optionsData={
														{
															preferredTherapistGender:
																optionsData?.preferredTherapistGender || [],
															apptDateTime: {
																min: optionsData?.apptDateTime?.min || null,
																max: optionsData?.apptDateTime?.max || null,
																message:
																	optionsData?.apptDateTime?.message || null,
															},
														} as any
													}
													onUpdateField={handleUpdateField}
													onProcessDateTimeChange={handleProcessDateTimeChange}
												/>
											</motion.div>
										);
									})}
								</AnimatePresence>
							</div>

							{form.formState.errors.root && (
								<div className="rounded-md border border-red-500/50 px-4 py-3 text-red-600">
									<p className="text-sm">
										<CircleAlertIcon
											aria-hidden="true"
											className="-mt-0.5 me-3 inline-flex opacity-60"
											size={16}
										/>
										{form.formState.errors.root.message}
									</p>
								</div>
							)}

							<FormActionButtons
								isLoading={isLoading}
								hasChanges={hasChanges}
								pageURL={pageURL}
								modifiedCount={changedVisits.length}
								onResetAll={resetAll}
							/>
						</form>
					</Form>
				</section>
			</FormPageContainer>

			<ConfirmationModal
				isOpen={isConfirmOpen}
				onClose={closeConfirmModal}
				onConfirm={onConfirmSubmit}
				isLoading={isLoading}
				summary={summary}
				reason={watchReason}
				onReasonChange={setReason}
				sendEmailNotification={watchSendEmail}
				onSendEmailNotificationChange={setSendEmail}
				bypassSlotConflict={watchBypassSlotConflict}
				onBypassSlotConflictChange={setBypassSlotConflict}
				bypassContractCheck={watchBypassContractCheck}
				onBypassContractCheckChange={setBypassContractCheck}
				bypassJourneyCheck={watchBypassJourneyCheck}
				onBypassJourneyCheckChange={setBypassJourneyCheck}
				mitraReason={mitraReason}
			/>

			<AlertDialog
				open={!!sortConfirmation}
				onOpenChange={(open) => !open && setSortConfirmation(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t(
								"bulk_reschedule.dialogs.sort.title",
								"Chronological Sorting Required",
							)}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t(
								"bulk_reschedule.dialogs.sort.description",
								"The new date you selected will change this visit's position in the series. Would you like to apply this change and re-sort all visits now?",
							)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setSortConfirmation(null)}>
							{t("bulk_reschedule.dialogs.sort.cancel", "Keep Current Order")}
						</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmSort}>
							{t("bulk_reschedule.dialogs.sort.confirm", "Yes, Apply and Sort")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={!!unscheduleConfirmation}
				onOpenChange={(open) => !open && setUnscheduleConfirmation(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t(
								"bulk_reschedule.dialogs.unschedule.title",
								"Unschedule Visit & Remove Therapist?",
							)}
						</AlertDialogTitle>
						<AlertDialogDescription className="space-y-2">
							<span className="block">
								{t(
									"bulk_reschedule.dialogs.unschedule.description",
									"Removing the date and time will also remove the assigned therapist. Additionally, the list will be re-sorted chronologically.",
								)}
							</span>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setUnscheduleConfirmation(null)}>
							{t("bulk_reschedule.dialogs.unschedule.cancel", "Cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive hover:bg-destructive/90"
							onClick={handleConfirmUnschedule}
						>
							{t(
								"bulk_reschedule.dialogs.unschedule.confirm",
								"Yes, Unschedule and Sort",
							)}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Fragment>
	);
}
